import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, Loader2, Eye, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import {
  uploadFileToStorage,
  MAX_UPLOAD_BYTES,
  FILE_TOO_LARGE_MESSAGE,
} from '@/lib/resumableUpload';

interface DocumentUploadButtonProps {
  documentId: string;
  documentName: string;
  onUploadComplete: () => void;
  size?: 'sm' | 'default';
  fileUrl?: string | null;
  hasUploadedFile?: boolean;
}

export function DocumentUploadButton({ 
  documentId, 
  documentName, 
  onUploadComplete,
  size = 'sm',
  fileUrl,
  hasUploadedFile = false
}: DocumentUploadButtonProps) {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [localFileUrl, setLocalFileUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset local state when documentId changes
  useEffect(() => {
    setLocalFileUrl(null);
  }, [documentId]);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleViewUploadedDocument = () => {
    const urlToOpen = localFileUrl || fileUrl;
    if (urlToOpen) {
      window.open(urlToOpen, '_blank');
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      toast({
        title: 'Invalid File Type',
        description: 'Please upload a PDF file.',
        variant: 'destructive',
      });
      return;
    }

    // Validate file size
    if (file.size > MAX_UPLOAD_BYTES) {
      toast({
        title: 'That file is too big',
        description: FILE_TOO_LARGE_MESSAGE,
        variant: 'destructive',
      });
      return;
    }

    setUploading(true);

    try {
      // Generate safe file name
      const safeName = documentName.replace(/[^a-z0-9\-]+/gi, '-').toLowerCase();
      const filePath = `generated/${safeName}-${documentId}-uploaded.pdf`;

      // Upload to storage. Big scans go up in resumable chunks.
      await uploadFileToStorage({
        bucket: 'documents',
        path: filePath,
        file,
        upsert: true,
        onProgress: setUploadPercent,
      });

      // Buckets are private — store path as reference
      setLocalFileUrl(filePath);

      // Update the generated_documents record with original filename
      const { error: updateError } = await supabase
        .from('generated_documents')
        .update({
          file_url: filePath,
          signed_file_url: filePath,
          file_path: filePath,
          file_size: file.size,
          status: 'final', // Mark as final since user uploaded their own version
          uploaded_filename: file.name, // Preserve user's original filename
        })
        .eq('id', documentId);

      if (updateError) throw updateError;

      toast({
        title: 'Document Uploaded',
        description: 'Your document has been uploaded and saved.',
      });

      onUploadComplete();
    } catch (error: any) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload Failed',
        description: error.message || 'Failed to upload document.',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
      setUploadPercent(0);
      // Reset the input so the same file can be uploaded again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Show "View Uploaded" button with replace option when document has been uploaded
  if ((hasUploadedFile && fileUrl) || localFileUrl) {
    return (
      <>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".pdf,application/pdf"
          className="hidden"
        />
        <div className="flex items-center">
          <Button
            variant="outline"
            size={size}
            onClick={handleViewUploadedDocument}
            className="rounded-r-none border-r-0"
            disabled={uploading}
          >
            {uploading ? (
              <>
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                {uploadPercent > 0 && uploadPercent < 100 ? `${uploadPercent}%` : 'Saving'}
              </>
            ) : (
              <>
                <Eye className="h-3 w-3 mr-1" />
                View Uploaded
              </>
            )}
          </Button>
          <Button
            variant="outline"
            size="icon"
            className={`rounded-l-none ${size === 'sm' ? 'h-8 w-8' : 'h-10 w-10'}`}
            onClick={handleClick}
            disabled={uploading}
            title="Replace with new upload"
          >
            <RefreshCw className={`${size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'}`} />
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".pdf,application/pdf"
        className="hidden"
      />
      <Button
        variant="outline"
        size={size}
        onClick={handleClick}
        disabled={uploading}
      >
        {uploading ? (
          <>
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            {uploadPercent > 0 && uploadPercent < 100 ? `${uploadPercent}%` : 'Saving'}
          </>
        ) : (
          <>
            <Upload className="h-3 w-3 mr-1" />
            Upload
          </>
        )}
      </Button>
    </>
  );
}
